import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  History, ArrowLeft, Filter, Trash2, Download, 
  CheckCircle, XCircle, Mail, Calendar, User
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CAMPAIGN_TYPES = {
  policy_renewal_reminder: { label: 'Renovación de Póliza', color: 'bg-purple-100 text-purple-800' },
  receipt_confirmation: { label: 'Confirmación de Pago', color: 'bg-green-100 text-green-800' },
  payment_reminder: { label: 'Recordatorio de Pago', color: 'bg-red-100 text-red-800' },
  birthday_greeting: { label: 'Cumpleaños', color: 'bg-yellow-100 text-yellow-800' },
  portal_access: { label: 'Acceso al Portal', color: 'bg-blue-100 text-blue-800' },
  special_announcement: { label: 'Comunicación Especial', color: 'bg-indigo-100 text-indigo-800' }
};

export default function EmailHistory() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [selectedLogs, setSelectedLogs] = useState([]);
  const [filters, setFilters] = useState({
    campaign_type: 'all',
    status: 'all',
    search: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filters, logs]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const [logsRes, campaignsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/email-campaigns/logs?limit=100`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${BACKEND_URL}/api/email-campaigns`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setLogs(logsRes.data);
      setCampaigns(campaignsRes.data);
      setFilteredLogs(logsRes.data);
    } catch (error) {
      console.error('Error fetching logs:', error);
      toast.error('Error al cargar historial');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...logs];

    // Filtrar por tipo de campaña
    if (filters.campaign_type !== 'all') {
      const campaignIds = campaigns
        .filter(c => c.campaign_type === filters.campaign_type)
        .map(c => c.id);
      
      filtered = filtered.filter(log => campaignIds.includes(log.campaign_id));
    }

    // Filtrar por estado
    if (filters.status !== 'all') {
      filtered = filtered.filter(log => log.status === filters.status);
    }

    // Filtrar por búsqueda
    if (filters.search) {
      const search = filters.search.toLowerCase();
      filtered = filtered.filter(log =>
        log.recipient_name.toLowerCase().includes(search) ||
        log.recipient_email.toLowerCase().includes(search) ||
        log.subject.toLowerCase().includes(search)
      );
    }

    setFilteredLogs(filtered);
  };

  const handleDeleteLog = async (logId) => {
    if (!window.confirm('¿Estás seguro de eliminar este registro?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/email-campaigns/logs/${logId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Registro eliminado');
      fetchData();
    } catch (error) {
      console.error('Error deleting log:', error);
      toast.error('Error al eliminar registro');
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedLogs(filteredLogs.map(log => log.id));
    } else {
      setSelectedLogs([]);
    }
  };

  const handleSelectLog = (logId, checked) => {
    if (checked) {
      setSelectedLogs([...selectedLogs, logId]);
    } else {
      setSelectedLogs(selectedLogs.filter(id => id !== logId));
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedLogs.length === 0) {
      toast.error('Selecciona al menos un registro para eliminar');
      return;
    }

    if (!window.confirm(`¿Estás seguro de eliminar ${selectedLogs.length} registro(s)? Esta acción no se puede deshacer.`)) return;

    try {
      const token = localStorage.getItem('token');
      
      // Eliminar cada log seleccionado
      await Promise.all(
        selectedLogs.map(logId =>
          axios.delete(`${BACKEND_URL}/api/email-campaigns/logs/${logId}`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        )
      );
      
      toast.success(`${selectedLogs.length} registro(s) eliminado(s)`);
      setSelectedLogs([]);
      fetchData();
    } catch (error) {
      console.error('Error deleting selected logs:', error);
      toast.error('Error al eliminar registros seleccionados');
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm('¿Estás seguro de eliminar TODO el historial? Esta acción no se puede deshacer.')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/email-campaigns/logs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Historial eliminado');
      setSelectedLogs([]);
      fetchData();
    } catch (error) {
      console.error('Error clearing logs:', error);
      toast.error('Error al limpiar historial');
    }
  };

  const getCampaignInfo = (campaignId) => {
    const campaign = campaigns.find(c => c.id === campaignId);
    if (!campaign) return { type: 'unknown', label: 'Desconocido', color: 'bg-gray-100 text-gray-800' };
    
    return {
      type: campaign.campaign_type,
      label: CAMPAIGN_TYPES[campaign.campaign_type]?.label || 'Desconocido',
      color: CAMPAIGN_TYPES[campaign.campaign_type]?.color || 'bg-gray-100 text-gray-800'
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <Button
          onClick={() => navigate('/email-campaigns')}
          variant="ghost"
          className="mb-4 gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Volver
        </Button>
        
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <History className="h-8 w-8 text-indigo-600" />
              Historial de Envíos
            </h1>
            <p className="text-gray-600 mt-2">
              {filteredLogs.length} de {logs.length} registros
            </p>
          </div>
          
          <div className="flex gap-3">
            <Button
              onClick={fetchData}
              variant="outline"
              className="gap-2"
            >
              Actualizar
            </Button>
            
            {selectedLogs.length > 0 && (
              <Button
                onClick={handleDeleteSelected}
                variant="outline"
                className="gap-2 text-orange-600 hover:text-orange-700 hover:bg-orange-50"
              >
                <Trash2 className="h-4 w-4" />
                Eliminar Seleccionados ({selectedLogs.length})
              </Button>
            )}
            
            {logs.length > 0 && (
              <Button
                onClick={handleClearAll}
                variant="outline"
                className="gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4" />
                Limpiar Todo
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Filtros */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Filter className="h-5 w-5" />
            Filtros
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Búsqueda */}
            <div>
              <label className="text-sm font-medium mb-2 block">Buscar</label>
              <Input
                placeholder="Nombre, email, asunto..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              />
            </div>

            {/* Tipo de campaña */}
            <div>
              <label className="text-sm font-medium mb-2 block">Tipo de Campaña</label>
              <Select
                value={filters.campaign_type}
                onValueChange={(value) => setFilters({ ...filters, campaign_type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las campañas</SelectItem>
                  <SelectItem value="policy_renewal_reminder">Renovación de Póliza</SelectItem>
                  <SelectItem value="receipt_confirmation">Confirmación de Pago</SelectItem>
                  <SelectItem value="payment_reminder">Recordatorio de Pago</SelectItem>
                  <SelectItem value="birthday_greeting">Cumpleaños</SelectItem>
                  <SelectItem value="portal_access">Acceso al Portal</SelectItem>
                  <SelectItem value="special_announcement">Comunicación Especial</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Estado */}
            <div>
              <label className="text-sm font-medium mb-2 block">Estado</label>
              <Select
                value={filters.status}
                onValueChange={(value) => setFilters({ ...filters, status: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="sent">Enviados</SelectItem>
                  <SelectItem value="failed">Fallidos</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Seleccionar todo */}
      {filteredLogs.length > 0 && (
        <div className="mb-4 flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
          <Checkbox
            checked={selectedLogs.length === filteredLogs.length && filteredLogs.length > 0}
            onCheckedChange={handleSelectAll}
            id="select-all"
          />
          <label htmlFor="select-all" className="text-sm font-medium cursor-pointer">
            Seleccionar todos ({filteredLogs.length} registros)
          </label>
        </div>
      )}

      {/* Lista de Logs */}
      {filteredLogs.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <History className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No hay registros
            </h3>
            <p className="text-gray-600">
              {filters.search || filters.campaign_type !== 'all' || filters.status !== 'all'
                ? 'No se encontraron registros con los filtros seleccionados'
                : 'Aún no se han enviado emails'
              }
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredLogs.map((log) => {
            const campaignInfo = getCampaignInfo(log.campaign_id);
            
            return (
              <Card key={log.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    {/* Checkbox */}
                    <div className="flex items-start pt-1">
                      <Checkbox
                        checked={selectedLogs.includes(log.id)}
                        onCheckedChange={(checked) => handleSelectLog(log.id, checked)}
                      />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      {/* Header */}
                      <div className="flex items-center gap-2 mb-2">
                        {log.status === 'sent' ? (
                          <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                        ) : (
                          <XCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
                        )}
                        
                        <Badge className={campaignInfo.color}>
                          {campaignInfo.label}
                        </Badge>
                        
                        <Badge variant={log.status === 'sent' ? 'default' : 'destructive'}>
                          {log.status === 'sent' ? 'Enviado' : 'Fallido'}
                        </Badge>
                      </div>

                      {/* Subject */}
                      <h3 className="font-medium text-gray-900 mb-2 truncate">
                        {log.subject}
                      </h3>

                      {/* Info */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4" />
                          <span className="truncate">{log.recipient_name}</span>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4" />
                          <span className="truncate">{log.recipient_email}</span>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4" />
                          <span>{new Date(log.sent_at).toLocaleString('es-MX')}</span>
                        </div>

                        {log.resend_message_id && (
                          <div className="text-xs font-mono text-gray-500 truncate">
                            ID: {log.resend_message_id}
                          </div>
                        )}
                      </div>

                      {/* Error */}
                      {log.error_details && (
                        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-800">
                          <strong>Error:</strong> {log.error_details}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteLog(log.id)}
                      className="flex-shrink-0"
                    >
                      <Trash2 className="h-4 w-4 text-gray-400 hover:text-red-600" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
